import { Image } from '@chakra-ui/react'
import React from 'react'
import Logo from 'src/images/logo.svg'
import settings from 'src/settings'

const logoWidth = 334 // 430
const logoHeight = 56 // 79

const sizes = {
  sm: 0.35,
  md: 0.51,
  lg: 0.9,
}

// Instance logos get their own heights, deliberately larger. The bundled
// Rolebase logo is very elongated (6:1), so its 56px reference height reads
// fine; YoCoop is 2.8:1 and CartoGroov 4:1, which at that same height look
// half the size. Height is what the eye compares, not width.
const instanceHeights = {
  sm: 26,
  md: 64,
  lg: 96,
}

interface Props {
  size?: keyof typeof sizes
  // Take most of the available width instead of following `size`. Used at the
  // bottom of the sidebar, whose width the user can drag — a percentage
  // follows it, a fixed size would not. 60%, settled by looking at it: full
  // width then 75% both read as oversized against the navigation above.
  fullWidth?: boolean
}

export default function BrandLogo({ size = 'md', fullWidth }: Props) {
  // An instance with its own logo shows it; everyone else keeps the bundled
  // SVG. Height fixed, width free (objectFit contain): the two instance logos
  // have different aspect ratios, so a fixed width would squash one of them.
  if (settings.brand.logoUrl) {
    return (
      <Image
        src={settings.brand.logoUrl}
        alt={settings.brand.name}
        {...(fullWidth
          ? { w: '60%', h: 'auto' }
          : { h: `${instanceHeights[size]}px` })}
        objectFit="contain"
      />
    )
  }

  // The bundled SVG carries fixed dimensions, so fullWidth does not apply to
  // it: instances without their own logo keep the sizes they always had.
  const ratio = sizes[size]
  return (
    <Logo width={`${logoWidth * ratio}px`} height={`${logoHeight * ratio}px`} />
  )
}
